import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Button,
  VStack,
  Progress,
  IconButton,
  Spinner,
  Icon,
  Alert,
  AlertIcon,
  useToast,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, CheckIcon, CloseIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { useMutation, useLazyQuery } from '@apollo/client';
import debounce from 'lodash/debounce';
import { SET_PASSWORD, CHECK_USERNAME_AVAILABLE } from '../queries';
import { validatePassword, validateUsername, getPasswordStrength } from '../utils';

interface SetPasswordFormProps {
  // Theme colors (optional - will use defaults if not provided)
  formLabelColor?: string;
  formFieldBg?: string;
  formFieldBorder?: string;
  formFieldBorderFocus?: string;
  textPrimary?: string;
  textSecondary?: string;
  textMuted?: string;
  accentBlue?: string;
  accentHover?: string;
  // Handlers
  onSuccess?: () => void;
  onCancel?: () => void;
}

/**
 * Set Password Form Component
 *
 * Allows existing users (who logged in via phone/email) to add
 * username/password credentials to their account.
 *
 * Features:
 * - Username input with real-time availability check
 * - Password input with strength indicator
 * - Confirm password validation
 * - Success feedback
 */
export const SetPasswordForm: React.FC<SetPasswordFormProps> = ({
  formLabelColor = 'gray.600',
  formFieldBg = 'white',
  formFieldBorder = 'gray.200',
  formFieldBorderFocus = 'blue.500',
  textPrimary = 'gray.800',
  textSecondary = 'gray.600',
  textMuted = 'gray.400',
  accentBlue = 'blue.500',
  accentHover = 'blue.600',
  onSuccess,
  onCancel,
}) => {
  const toast = useToast();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // GraphQL
  const [setPasswordMutation, { loading }] = useMutation(SET_PASSWORD);
  const [checkUsernameQuery] = useLazyQuery(CHECK_USERNAME_AVAILABLE);

  // Password strength
  const passwordStrength = getPasswordStrength(password);

  // Debounced username availability check
  const checkUsernameAvailability = useMemo(
    () =>
      debounce(async (usernameToCheck: string) => {
        if (usernameToCheck.length < 3) {
          setUsernameAvailable(null);
          setCheckingUsername(false);
          return;
        }

        const usernameError = validateUsername(usernameToCheck);
        if (usernameError) {
          setUsernameAvailable(null);
          setCheckingUsername(false);
          return;
        }

        setCheckingUsername(true);
        try {
          const { data } = await checkUsernameQuery({
            variables: { username: usernameToCheck },
          });
          setUsernameAvailable(data?.checkUsernameAvailable ?? null);
        } catch (err) {
          console.error('Error checking username:', err);
          setUsernameAvailable(null);
        } finally {
          setCheckingUsername(false);
        }
      }, 500),
    [checkUsernameQuery]
  );

  // Cancel debounce on unmount
  useEffect(() => {
    return () => {
      checkUsernameAvailability.cancel();
    };
  }, [checkUsernameAvailability]);

  // Check username when it changes
  useEffect(() => {
    if (username.length >= 3) {
      checkUsernameAvailability(username);
    } else {
      setUsernameAvailable(null);
    }
  }, [username, checkUsernameAvailability]);

  // Username status indicator
  const renderUsernameStatus = () => {
    if (username.length < 3) return null;

    if (checkingUsername) {
      return <Spinner size="xs" color={accentBlue} />;
    }

    if (usernameAvailable === true) {
      return <Icon as={CheckIcon} color="green.500" />;
    }

    if (usernameAvailable === false) {
      return <Icon as={CloseIcon} color="red.500" />;
    }

    return null;
  };

  // Username helper text
  const getUsernameHelperText = () => {
    if (checkingUsername) {
      return 'Checking availability...';
    }

    if (usernameAvailable === true) {
      return 'Username is available';
    }

    if (usernameAvailable === false) {
      return 'Username is already taken';
    }

    if (username.length > 0 && username.length < 3) {
      return 'Username must be at least 3 characters';
    }

    return 'Letters, numbers, and underscores only';
  };

  // Form validation
  const validateForm = (): string | null => {
    const usernameError = validateUsername(username);
    if (usernameError) return usernameError;

    if (usernameAvailable === false) {
      return 'Username is already taken';
    }

    const passwordError = validatePassword(password);
    if (passwordError) return passwordError;

    if (!confirmPassword) {
      return 'Please confirm your password';
    }

    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }

    return null;
  };

  // Handle submit
  const handleSubmit = async () => {
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      console.log('🔐 [SET-PWD-1] Setting password for user');

      const { data } = await setPasswordMutation({
        variables: {
          input: {
            username: username.toLowerCase(),
            password,
          },
        },
      });

      if (data?.setPassword) {
        console.log('✅ [SET-PWD-2] Password set successfully');
        setSuccess(true);
        toast({
          title: 'Password Set',
          description: 'You can now sign in with your username and password.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 1500);
        }
      }
    } catch (err: any) {
      console.error('❌ [SET-PWD-ERROR]', err);
      if (err.message?.includes('already taken')) {
        setError('Username is already taken');
        setUsernameAvailable(false);
      } else {
        setError(err.message || 'Failed to set password');
      }
    }
  };

  // Success state
  if (success) {
    return (
      <VStack spacing={4} align="center" py={4}>
        <CheckCircleIcon boxSize={12} color="green.500" />
        <Text color={textPrimary} fontWeight="bold" fontSize="lg">
          Password Set Successfully
        </Text>
        <Text color={textSecondary} textAlign="center">
          You can now sign in with your username and password.
        </Text>
      </VStack>
    );
  }

  return (
    <Box>
      <VStack spacing={4} align="stretch">
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            Add a username and password to sign in faster next time.
          </Text>
        </Alert>

        {/* Username Field */}
        <FormControl isInvalid={usernameAvailable === false}>
          <FormLabel color={formLabelColor}>Username</FormLabel>
          <InputGroup>
            <Input
              type="text"
              placeholder="your_username"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase())}
              autoFocus
              autoComplete="username"
              bg={formFieldBg}
              color={textPrimary}
              borderColor={formFieldBorder}
              _placeholder={{ color: textMuted }}
              _focus={{ borderColor: formFieldBorderFocus }}
              _hover={{ borderColor: formFieldBorderFocus }}
            />
            <InputRightElement>
              {renderUsernameStatus()}
            </InputRightElement>
          </InputGroup>
          <FormHelperText
            color={
              usernameAvailable === true
                ? 'green.500'
                : usernameAvailable === false
                ? 'red.500'
                : textMuted
            }
          >
            {getUsernameHelperText()}
          </FormHelperText>
        </FormControl>

        {/* Password Field */}
        <FormControl>
          <FormLabel color={formLabelColor}>Password</FormLabel>
          <InputGroup>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              bg={formFieldBg}
              color={textPrimary}
              borderColor={formFieldBorder}
              _placeholder={{ color: textMuted }}
              _focus={{ borderColor: formFieldBorderFocus }}
              _hover={{ borderColor: formFieldBorderFocus }}
            />
            <InputRightElement>
              <IconButton
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                variant="ghost"
                size="sm"
                onClick={() => setShowPassword(!showPassword)}
                color={textMuted}
                _hover={{ color: textPrimary }}
              />
            </InputRightElement>
          </InputGroup>

          {/* Password strength indicator */}
          {password.length > 0 && (
            <Box mt={2}>
              <Progress
                value={(passwordStrength.score / 4) * 100}
                size="xs"
                colorScheme={
                  passwordStrength.score <= 1
                    ? 'red'
                    : passwordStrength.score === 2
                    ? 'yellow'
                    : 'green'
                }
                borderRadius="full"
              />
              <Text fontSize="xs" color={passwordStrength.color} mt={1}>
                Password strength: {passwordStrength.label}
              </Text>
            </Box>
          )}
        </FormControl>

        {/* Confirm Password Field */}
        <FormControl
          isInvalid={confirmPassword.length > 0 && password !== confirmPassword}
        >
          <FormLabel color={formLabelColor}>Confirm Password</FormLabel>
          <InputGroup>
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
              autoComplete="new-password"
              bg={formFieldBg}
              color={textPrimary}
              borderColor={formFieldBorder}
              _placeholder={{ color: textMuted }}
              _focus={{ borderColor: formFieldBorderFocus }}
              _hover={{ borderColor: formFieldBorderFocus }}
            />
            <InputRightElement>
              <IconButton
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                variant="ghost"
                size="sm"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                color={textMuted}
                _hover={{ color: textPrimary }}
              />
            </InputRightElement>
          </InputGroup>
          {confirmPassword.length > 0 && password !== confirmPassword && (
            <FormErrorMessage>Passwords do not match</FormErrorMessage>
          )}
        </FormControl>

        {/* Error message */}
        {error && (
          <Text color="red.500" fontSize="sm" textAlign="center">
            {error}
          </Text>
        )}

        {/* Submit button */}
        <Button
          bg={accentBlue}
          color="white"
          width="100%"
          mt={2}
          onClick={handleSubmit}
          isLoading={loading}
          isDisabled={usernameAvailable === false}
          _hover={{ bg: accentHover }}
        >
          Set Password
        </Button>

        {/* Cancel button */}
        {onCancel && (
          <Button
            variant="ghost"
            width="100%"
            onClick={onCancel}
            color={textSecondary}
          >
            Cancel
          </Button>
        )}
      </VStack>
    </Box>
  );
};
